import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  X,
  HardHat,
  FileWarning,
  ShieldAlert,
  BarChart3,
  Building2,
  Tablet,
  Bell,
  User as UserIcon,
  Settings as SettingsIcon,
  Sparkles,
  LogOut,
  Crown,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * v1.2 — Mobile "More" drawer surfaced from BottomNav.
 * Lists every operational route the role can access PLUS Settings, Setup checklist
 * and Sign out. Role-gated.
 */

const ROLE_LEVEL = { host: 1, receptionist: 2, site_manager: 3, org_admin: 4, super_admin: 5 };

const ITEMS = [
  { to: "/contractors",          label: "Contractors",       icon: HardHat,       minRole: "site_manager" },
  { to: "/permits-to-work",      label: "Permits",           icon: FileWarning,   minRole: "site_manager" },
  { to: "/admin/watchlist-hits", label: "Watchlist",         icon: ShieldAlert,   minRole: "receptionist" },
  { to: "/reports",              label: "Reports",           icon: BarChart3,     minRole: "receptionist" },
  { to: "/sites",                label: "Sites",             icon: Building2,     minRole: "host" },
  { to: "/kiosks",               label: "Kiosks",            icon: Tablet,        minRole: "site_manager" },
  { to: "/reception/mobile",     label: "Reception (mobile)", icon: Tablet,       minRole: "receptionist" },
  { to: "/notifications",        label: "Notifications",     icon: Bell,          minRole: "host" },
];

export default function MoreDrawer({ open, onClose }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (!open) return null;
  const role = user?.role || "host";
  const myLevel = ROLE_LEVEL[role] || 0;
  const visible = ITEMS.filter((it) => myLevel >= ROLE_LEVEL[it.minRole]);

  const onItem = (to) => () => { onClose(); nav(to); };

  return (
    <div className="md:hidden fixed inset-0 z-50" data-testid="more-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[80vh] overflow-y-auto pb-safe">
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy-100">
          <div>
            <div className="text-xs text-navy-500">Signed in as</div>
            <div className="text-sm font-semibold text-navy-900 truncate">{user?.name || user?.email}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-navy-50" data-testid="more-drawer-close">
            <X className="w-5 h-5 text-navy-500" />
          </button>
        </div>
        <nav className="py-2">
          {visible.map((it) => {
            const Icon = it.icon;
            return (
              <button
                key={it.to}
                onClick={onItem(it.to)}
                className="w-full text-left px-5 py-3 text-sm text-navy-800 hover:bg-navy-50 inline-flex items-center gap-3"
                data-testid={`more-${it.to.replace(/\//g, "-")}`}
              >
                <Icon className="w-4 h-4 text-navy-500" /> {it.label}
              </button>
            );
          })}
          {role === "super_admin" && (
            <button
              onClick={onItem("/super-admin")}
              className="w-full text-left px-5 py-3 text-sm text-[#FF8A4C] font-medium hover:bg-navy-50 inline-flex items-center gap-3 border-t border-navy-100"
              data-testid="more-super-admin"
            >
              <Crown className="w-4 h-4" /> Super admin
            </button>
          )}
        </nav>
        <div className="border-t border-navy-100 py-2">
          <button onClick={onItem("/account/profile")} className="w-full text-left px-5 py-3 text-sm text-navy-800 hover:bg-navy-50 inline-flex items-center gap-3" data-testid="more-profile">
            <UserIcon className="w-4 h-4 text-navy-500" /> My profile
          </button>
          <button onClick={onItem("/onboarding")} className="w-full text-left px-5 py-3 text-sm text-navy-800 hover:bg-navy-50 inline-flex items-center gap-3" data-testid="more-setup">
            <Sparkles className="w-4 h-4 text-navy-500" /> Setup checklist
          </button>
          <button onClick={onItem("/settings")} className="w-full text-left px-5 py-3 text-sm text-navy-800 hover:bg-navy-50 inline-flex items-center gap-3" data-testid="more-settings">
            <SettingsIcon className="w-4 h-4 text-navy-500" /> Settings
          </button>
        </div>
        <div className="border-t border-navy-100 py-2">
          <button onClick={() => { onClose(); logout(); }} className="w-full text-left px-5 py-3 text-sm text-danger hover:bg-danger/5 inline-flex items-center gap-3" data-testid="more-logout">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
